/* eslint-disable no-param-reassign */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import merchantsApi from 'services/merchants';
import { MerchantInfo, MerchantPayoutFlow, MerchantStatus } from 'services/merchants/types';
import { undecorateMerchantData } from 'services/merchants/utils';

import { CurrencyCode } from 'types/currency';

interface MerchantState {
  merchant: MerchantInfo;
}

const SLICE_NAME = 'merchant';

const initialState: MerchantState = {
  merchant: {
    uuid: '',
    name: '',
    createdAt: '',
    bridgeCustomerId: '',
    mainBankAccountUuid: null,
    status: MerchantStatus.Active,
    shift4MerchantId: null,
    ownerEmail: '',
    ownerFullName: '',
    processingFee: 0,
    webhookUrl: null,
    meshSubclientId: null,
    title: null,
    lightModeLogoUrl: '',
    darkModeLogoUrl: '',
    acceptedTOS: [],
    onboardingToken: null,
    refundWalletBalance: '0',
    cryptoPayoutBalances: {
      total: '0',
      inProgress: '0',
      available: '0',
    },
    payout: {
      nextPayoutAmount: '0',
      nextPayout: '',
    },
    kyc: {},
    payoutFlow: MerchantPayoutFlow.SETTLEMENT_WALLET,
    payoutSchedule: null,
    mainFiatCurrency: CurrencyCode.USD,
    settlementWalletBalance: '0',
    pwcEnabled: true,
    stablecoinSettlementEnabled: true,
    cryptoPayoutEnabled: true,
  },
};

export const merchantSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setMerchant: (state, action: PayloadAction<{ merchant: MerchantInfo } | undefined>) => {
      if (!action.payload) {
        state.merchant = initialState.merchant;
        return;
      }

      state.merchant = undecorateMerchantData(action.payload.merchant);
    },
    updateMerchantInfo: (state, action: PayloadAction<Partial<MerchantInfo>>) => {
      state.merchant = {
        ...state.merchant,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(merchantsApi.endpoints.getMerchant.matchFulfilled, (state, { payload }) => {
      const record = payload?.data;
      if (!record) {
        return;
      }

      state.merchant = undecorateMerchantData(record);
    });
  },
});

export const { updateMerchantInfo } = merchantSlice.actions;

export default merchantSlice.reducer;
